import omit from 'lodash/omit';
import pick from 'lodash/pick';
import { GeoCoordinates, Status } from '@hotelplan/graphql.types';
import { useObjectId } from '@hotelplan/libs.context.object-id';
import { mapSrlFilterValuesToSrlFilterCriteriaInput } from 'components/domain/filters/Filters.mappers';
import usePDPSearchValues from 'components/domain/pdp/usePDPSearchValues';
import { mapSearchControlToCriteria } from 'components/domain/search-control/components/SearchControlForm/SearchControl.mappers';
import { useGetPdpMapQuery } from 'graphql/pdp/GetPdpMap.generated';
import {
  GetSrlMapPinsQuery,
  useGetSrlMapPinsQuery,
} from 'graphql/srl/map/GetSRLMapPins.generated';
import { SrlMapProductPinFragment } from 'graphql/srl/map/SrlMapProductPin.generated';

const PDP_RADIUS_METERS = 10000;

const onGetSrlMapPinsCompleted = (completedData: GetSrlMapPinsQuery): void => {
  const pinsData = completedData.srl.search.mapPins;

  if (pinsData.status === Status.Failure) {
    // eslint-disable-next-line no-console
    console.error('GetSrlMapPins Failure' as const, pinsData.message);
  }
};

export default function usePDPHotelMapItems(
  showNearBy = false,
  mapLoaded = false
) {
  const { id } = useObjectId();
  const { data: coordinatesData } = useGetPdpMapQuery({
    variables: { id },
  });
  const { loading, searchControl, filters } = usePDPSearchValues();
  const hotelCoordinates = coordinatesData?.pdp.map.hotel.coordinates;

  const searchControlInputOriginal = mapSearchControlToCriteria(searchControl);
  const searchControlInput = { ...searchControlInputOriginal };
  const filtersInput = mapSrlFilterValuesToSrlFilterCriteriaInput(filters);

  if (showNearBy) {
    searchControlInput.travelDestinations = null;
    filtersInput.radius = {
      center: pick(hotelCoordinates as GeoCoordinates, [
        'longitude',
        'latitude',
      ]),
      radius: PDP_RADIUS_METERS,
    };
  }

  const { data: hotelPinData, loading: hotelPinLoading } =
    useGetSrlMapPinsQuery({
      skip: loading || !mapLoaded,
      variables: {
        searchControl: searchControlInputOriginal,
        filters: omit(filtersInput, 'radius' as const),
      },
      onCompleted: onGetSrlMapPinsCompleted,
    });

  const hotel = hotelPinData?.srl.search.mapPins.productPins.find(
    pin => pin.id === id
  );

  const { data: pinsData, loading: pinsLoading } = useGetSrlMapPinsQuery({
    skip: (showNearBy ? loading || !hotelCoordinates : loading) || !mapLoaded,
    variables: {
      searchControl: searchControlInput,
      filters: filtersInput,
      forceSingleView: showNearBy,
    },
    onCompleted: onGetSrlMapPinsCompleted,
  });

  const pins = pinsData?.srl.search.mapPins;
  const productPins = pins?.productPins || ([] as SrlMapProductPinFragment[]);

  let products = showNearBy ? productPins : [];

  if (hotel) {
    // Put current hotel at the end of products list
    // To show it above the others
    products = products.filter(product => product.id !== id);
    products.push(hotel);
  }

  return {
    products,
    loading: hotelPinLoading || pinsLoading,
  };
}
